import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, Target, MousePointerClick, ClipboardList, ShieldCheck, CreditCard, CheckCircle2, IndianRupee, DollarSign, Calendar, Plus, Trash2, Settings2 } from "lucide-react";

/* ---------- helpers ---------- */

function fmtPct(v) {
    if (!v && v !== 0) return "—";
    return `${(v * 100).toFixed(1)}%`;
}

function fmtNum(v) {
    return (v || 0).toLocaleString();
}

function fmtMoney(currency, amount) {
    if (!amount && amount !== 0) return "—";
    const symbol = currency === "INR" ? "₹" : "$";
    return `${symbol}${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function fmtRoas(v) {
    if (v === null || v === undefined) return "—";
    return `${Number(v).toFixed(2)}×`;
}

const STATUS_META = {
    strong: { label: "Strong", color: "#4ade80", bg: "rgba(74,222,128,0.12)" },
    watch: { label: "Watch", color: "#eab308", bg: "rgba(234,179,8,0.14)" },
    needs_optimisation: { label: "Needs optimisation", color: "#f97316", bg: "rgba(249,115,22,0.14)" },
    no_paid: { label: "No paid yet", color: "#94a3b8", bg: "rgba(148,163,184,0.14)" },
    no_spend: { label: "No spend", color: "#64748b", bg: "rgba(100,116,139,0.14)" },
};

function StatusPill({ status }) {
    const s = STATUS_META[status] || STATUS_META.no_spend;
    return (
        <span
            className="inline-flex items-center px-2 py-1 label-mono text-[9px]"
            style={{ background: s.bg, color: s.color, border: `1px solid ${s.color}66` }}
            data-testid={`status-pill-${status}`}
        >
            {s.label}
        </span>
    );
}

/* ---------- Kpi card ---------- */

function KpiCard({ icon: Icon, label, value, sub, primary = false, testid }) {
    return (
        <div
            data-testid={testid}
            className={`p-5 md:p-6 border-r border-b border-[rgba(255,255,240,0.12)] ${primary ? "bg-[rgba(201,162,75,0.08)]" : ""}`}
        >
            <div className="flex items-center gap-2 label-mono text-[10px] text-[var(--ivory)]/60">
                <Icon className="w-3.5 h-3.5" />
                {label}
            </div>
            <div className={`serif-display mt-3 leading-none ${primary ? "text-4xl md:text-5xl text-[var(--gold)]" : "text-3xl md:text-4xl text-[var(--ivory)]"}`}>
                {value}
            </div>
            {sub && <div className="label-mono text-[10px] mt-2 text-[var(--ivory)]/50">{sub}</div>}
        </div>
    );
}

/* ---------- Breakdown table ---------- */

function BreakdownTable({ title, rows, keyLabel, testid }) {
    if (!rows || rows.length === 0) {
        return (
            <div className="border border-[rgba(255,255,240,0.12)] p-6" data-testid={testid}>
                <div className="label-mono text-[var(--ivory)]/60">{title}</div>
                <p className="text-sm text-[var(--ivory)]/40 mt-4">No data yet in this range.</p>
            </div>
        );
    }
    return (
        <div className="border border-[rgba(255,255,240,0.12)]" data-testid={testid}>
            <div className="label-mono text-[var(--ivory)]/70 p-4 border-b border-[rgba(255,255,240,0.12)]">
                {title}
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="text-[var(--ivory)]/50 label-mono text-[10px]">
                        <tr>
                            <th className="text-left px-4 py-3 font-normal">{keyLabel}</th>
                            <th className="text-right px-4 py-3 font-normal">Visitors</th>
                            <th className="text-right px-4 py-3 font-normal">Applications</th>
                            <th className="text-right px-4 py-3 font-normal">Paid</th>
                            <th className="text-right px-4 py-3 font-normal">Conv. rate</th>
                            <th className="text-right px-4 py-3 font-normal">Revenue</th>
                        </tr>
                    </thead>
                    <tbody className="text-[var(--ivory)]/85">
                        {rows.map((r) => {
                            const rev = [];
                            if (r.revenue_inr) rev.push(fmtMoney("INR", r.revenue_inr));
                            if (r.revenue_usd) rev.push(fmtMoney("USD", r.revenue_usd));
                            return (
                                <tr key={r.key} className="border-t border-[rgba(255,255,240,0.06)]">
                                    <td className="px-4 py-3 font-mono text-xs">{r.key}</td>
                                    <td className="px-4 py-3 text-right">{fmtNum(r.visitors)}</td>
                                    <td className="px-4 py-3 text-right">{fmtNum(r.applications)}</td>
                                    <td className="px-4 py-3 text-right text-[var(--gold)] font-semibold">{fmtNum(r.paid)}</td>
                                    <td className="px-4 py-3 text-right">{fmtPct(r.conversion_rate)}</td>
                                    <td className="px-4 py-3 text-right label-mono text-[10px]">{rev.join(" · ") || "—"}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/* ---------- Campaign breakdown with CPA/ROAS ---------- */

function CampaignPerformanceTable({ rows, thresholds, testid = "breakdown-utm-campaign" }) {
    if (!rows || rows.length === 0) {
        return (
            <div className="border border-[rgba(255,255,240,0.12)] p-6" data-testid={testid}>
                <div className="label-mono text-[var(--ivory)]/60">By UTM Campaign</div>
                <p className="text-sm text-[var(--ivory)]/40 mt-4">No data yet in this range.</p>
            </div>
        );
    }
    return (
        <div className="border border-[rgba(255,255,240,0.12)]" data-testid={testid}>
            <div className="flex items-center justify-between p-4 border-b border-[rgba(255,255,240,0.12)]">
                <div className="label-mono text-[var(--ivory)]/70">By UTM Campaign · CPA + ROAS</div>
                <div className="label-mono text-[10px] text-[var(--ivory)]/40">
                    Strong ≤ ₹{thresholds?.strong_max} · Watch ≤ ₹{thresholds?.watch_max} · Optimise &gt; ₹{thresholds?.watch_max}
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="text-[var(--ivory)]/50 label-mono text-[10px]">
                        <tr>
                            <th className="text-left px-4 py-3 font-normal">utm_campaign</th>
                            <th className="text-right px-4 py-3 font-normal">Ad Spend</th>
                            <th className="text-right px-4 py-3 font-normal">Visitors</th>
                            <th className="text-right px-4 py-3 font-normal">Started</th>
                            <th className="text-right px-4 py-3 font-normal">Paid</th>
                            <th className="text-right px-4 py-3 font-normal">Revenue</th>
                            <th className="text-right px-4 py-3 font-normal">CPA</th>
                            <th className="text-right px-4 py-3 font-normal">ROAS</th>
                            <th className="text-right px-4 py-3 font-normal">LP→Paid</th>
                            <th className="text-right px-4 py-3 font-normal">Status</th>
                        </tr>
                    </thead>
                    <tbody className="text-[var(--ivory)]/85">
                        {rows.map((r) => {
                            const rev = [];
                            if (r.revenue_inr) rev.push(fmtMoney("INR", r.revenue_inr));
                            if (r.revenue_usd) rev.push(fmtMoney("USD", r.revenue_usd));
                            return (
                                <tr key={r.key} className="border-t border-[rgba(255,255,240,0.06)]" data-testid={`campaign-row-${r.key}`}>
                                    <td className="px-4 py-3">
                                        <div className="font-mono text-xs text-[var(--ivory)]">{r.key}</div>
                                        {r.platforms && r.platforms.length > 0 && (
                                            <div className="label-mono text-[9px] text-[var(--ivory)]/50 mt-1">{r.platforms.join(" · ")}</div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-right label-mono text-[10px]">
                                        {r.ad_spend_total_inr > 0 ? fmtMoney("INR", r.ad_spend_total_inr) : "—"}
                                    </td>
                                    <td className="px-4 py-3 text-right">{fmtNum(r.visitors)}</td>
                                    <td className="px-4 py-3 text-right">{fmtNum(r.applications)}</td>
                                    <td className="px-4 py-3 text-right text-[var(--gold)] font-semibold">{fmtNum(r.paid)}</td>
                                    <td className="px-4 py-3 text-right label-mono text-[10px]">{rev.join(" · ") || "—"}</td>
                                    <td className="px-4 py-3 text-right">
                                        {r.cpa_inr === null || r.cpa_inr === undefined ? "—" : `₹${Number(r.cpa_inr).toLocaleString()}`}
                                    </td>
                                    <td className="px-4 py-3 text-right">{fmtRoas(r.roas)}</td>
                                    <td className="px-4 py-3 text-right">{fmtPct(r.lp_to_paid_rate)}</td>
                                    <td className="px-4 py-3 text-right"><StatusPill status={r.status} /></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/* ---------- Ad Spend admin panel ---------- */

function AdSpendPanel({ onChange }) {
    const [items, setItems] = useState([]);
    const [thresholds, setThresholds] = useState({ strong_max: 300, watch_max: 500, currency: "INR" });
    const [thrEditing, setThrEditing] = useState(false);
    const [form, setForm] = useState({
        date: new Date().toISOString().slice(0, 10),
        utm_campaign: "",
        platform: "meta",
        amount: "",
        currency: "INR",
        notes: "",
    });
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const [a, b] = await Promise.all([
                api.get("/admin/ad-spend"),
                api.get("/admin/ad-spend/thresholds"),
            ]);
            setItems(a.data?.items || []);
            setThresholds(b.data || thresholds);
        } catch (_e) {
            toast.error("Could not load ad spend");
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.utm_campaign || !form.amount) {
            toast.error("Campaign and amount are required");
            return;
        }
        setSaving(true);
        try {
            await api.post("/admin/ad-spend", { ...form, amount: parseFloat(form.amount) });
            toast.success("Ad spend recorded");
            setForm({ ...form, utm_campaign: "", amount: "", notes: "" });
            await load();
            onChange && onChange();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save");
        } finally { setSaving(false); }
    };

    const remove = async (id) => {
        if (!confirm("Delete this spend entry?")) return;
        try {
            await api.delete(`/admin/ad-spend/${id}`);
            toast.success("Deleted");
            await load();
            onChange && onChange();
        } catch (_e) {
            toast.error("Could not delete");
        }
    };

    const saveThresholds = async (e) => {
        e.preventDefault();
        try {
            await api.put("/admin/ad-spend/thresholds", {
                strong_max: parseFloat(thresholds.strong_max),
                watch_max: parseFloat(thresholds.watch_max),
                currency: thresholds.currency,
            });
            toast.success("Thresholds updated");
            setThrEditing(false);
            onChange && onChange();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save thresholds");
        }
    };

    return (
        <div className="border border-[rgba(255,255,240,0.12)]" data-testid="ad-spend-panel">
            <div className="p-4 border-b border-[rgba(255,255,240,0.12)] flex items-center justify-between">
                <div className="label-mono text-[var(--ivory)]/70">Ad Spend · Manual Entry</div>
                <button
                    onClick={() => setThrEditing((v) => !v)}
                    className="label-mono text-[10px] inline-flex items-center gap-1.5 text-[var(--ivory)]/60 hover:text-[var(--gold)]"
                    data-testid="toggle-thresholds"
                >
                    <Settings2 className="w-3.5 h-3.5" /> CPA Thresholds
                </button>
            </div>

            {thrEditing && (
                <form onSubmit={saveThresholds} className="p-4 border-b border-[rgba(255,255,240,0.12)] bg-[rgba(201,162,75,0.04)] flex flex-wrap items-end gap-3" data-testid="thresholds-form">
                    <div>
                        <div className="label-mono text-[10px] text-[var(--ivory)]/50 mb-1">Strong ≤ (₹)</div>
                        <input type="number" min="1" step="1" className="input-editorial max-w-[130px]" value={thresholds.strong_max} onChange={(e) => setThresholds({ ...thresholds, strong_max: e.target.value })} data-testid="threshold-strong" />
                    </div>
                    <div>
                        <div className="label-mono text-[10px] text-[var(--ivory)]/50 mb-1">Watch ≤ (₹)</div>
                        <input type="number" min="1" step="1" className="input-editorial max-w-[130px]" value={thresholds.watch_max} onChange={(e) => setThresholds({ ...thresholds, watch_max: e.target.value })} data-testid="threshold-watch" />
                    </div>
                    <button type="submit" className="btn-gold" data-testid="thresholds-save">Save thresholds</button>
                    <div className="label-mono text-[10px] text-[var(--ivory)]/40 basis-full">
                        Anything above Watch → Needs Optimisation. Thresholds are advisory only — they never pause or change any live ad campaign.
                    </div>
                </form>
            )}

            <form onSubmit={submit} className="p-4 grid grid-cols-2 md:grid-cols-6 gap-3 border-b border-[rgba(255,255,240,0.12)]" data-testid="ad-spend-form">
                <div>
                    <div className="label-mono text-[10px] text-[var(--ivory)]/50 mb-1">Date</div>
                    <input type="date" className="input-editorial" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="spend-date" />
                </div>
                <div className="col-span-2">
                    <div className="label-mono text-[10px] text-[var(--ivory)]/50 mb-1">UTM Campaign</div>
                    <input type="text" className="input-editorial" placeholder="e.g. cps26-launch" value={form.utm_campaign} onChange={(e) => setForm({ ...form, utm_campaign: e.target.value })} required data-testid="spend-campaign" />
                </div>
                <div>
                    <div className="label-mono text-[10px] text-[var(--ivory)]/50 mb-1">Platform</div>
                    <select className="input-editorial" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} data-testid="spend-platform">
                        <option value="meta">Meta</option>
                        <option value="google">Google</option>
                        <option value="linkedin">LinkedIn</option>
                        <option value="other">Other</option>
                    </select>
                </div>
                <div>
                    <div className="label-mono text-[10px] text-[var(--ivory)]/50 mb-1">Amount</div>
                    <input type="number" step="0.01" min="0" className="input-editorial" placeholder="0.00" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required data-testid="spend-amount" />
                </div>
                <div>
                    <div className="label-mono text-[10px] text-[var(--ivory)]/50 mb-1">Currency</div>
                    <select className="input-editorial" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="spend-currency">
                        <option value="INR">INR</option>
                        <option value="USD">USD</option>
                    </select>
                </div>
                <div className="col-span-2 md:col-span-6 flex flex-wrap items-end gap-3">
                    <div className="flex-1 min-w-[240px]">
                        <div className="label-mono text-[10px] text-[var(--ivory)]/50 mb-1">Notes (optional)</div>
                        <input type="text" className="input-editorial" placeholder="Ad-set name, creative id, anything worth remembering…" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="spend-notes" />
                    </div>
                    <button type="submit" disabled={saving} className="btn-gold inline-flex items-center gap-2 disabled:opacity-40" data-testid="spend-submit">
                        <Plus className="w-4 h-4" /> {saving ? "Saving…" : "Add spend"}
                    </button>
                </div>
            </form>

            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="text-[var(--ivory)]/50 label-mono text-[10px]">
                        <tr>
                            <th className="text-left px-4 py-3 font-normal">Date</th>
                            <th className="text-left px-4 py-3 font-normal">Campaign</th>
                            <th className="text-left px-4 py-3 font-normal">Platform</th>
                            <th className="text-right px-4 py-3 font-normal">Amount</th>
                            <th className="text-left px-4 py-3 font-normal">Notes</th>
                            <th className="text-left px-4 py-3 font-normal">Entered by</th>
                            <th className="text-right px-4 py-3 font-normal"></th>
                        </tr>
                    </thead>
                    <tbody className="text-[var(--ivory)]/80">
                        {items.length === 0 && (
                            <tr><td colSpan={7} className="px-4 py-6 text-[var(--ivory)]/40 text-center">No spend entries yet.</td></tr>
                        )}
                        {items.map((r) => (
                            <tr key={r.id} className="border-t border-[rgba(255,255,240,0.06)]">
                                <td className="px-4 py-2 label-mono text-[10px]">{r.date}</td>
                                <td className="px-4 py-2 font-mono text-xs">{r.utm_campaign}</td>
                                <td className="px-4 py-2 label-mono text-[10px] capitalize">{r.platform}</td>
                                <td className="px-4 py-2 text-right label-mono text-[10px]">{fmtMoney(r.currency, r.amount)}</td>
                                <td className="px-4 py-2 text-xs text-[var(--ivory)]/60">{r.notes || "—"}</td>
                                <td className="px-4 py-2 text-xs text-[var(--ivory)]/50">{r.created_by || "—"}</td>
                                <td className="px-4 py-2 text-right">
                                    <button onClick={() => remove(r.id)} className="text-[var(--ivory)]/50 hover:text-red-400 transition-colors" data-testid={`spend-delete-${r.id}`} title="Delete entry">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/* ---------- Main component ---------- */

const RANGE_OPTIONS = [
    { key: "today", label: "Today" },
    { key: "7d", label: "Last 7 days" },
    { key: "30d", label: "Last 30 days" },
    { key: "custom", label: "Custom range" },
];

export default function CampaignPerformance() {
    const [range, setRange] = useState("30d");
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { range };
            if (range === "custom") {
                if (!start || !end) {
                    toast.error("Pick both a start and end date");
                    setLoading(false);
                    return;
                }
                params.start = new Date(start).toISOString();
                params.end = new Date(end + "T23:59:59").toISOString();
            }
            const { data } = await api.get("/admin/analytics/campaign-performance", { params });
            setData(data);
        } catch (e) {
            toast.error("Could not load campaign performance");
        } finally {
            setLoading(false);
        }
    }, [range, start, end]);

    useEffect(() => {
        if (range !== "custom") load();
    }, [range, load]);

    const a = data?.aggregate;

    return (
        <div data-testid="campaign-performance-panel" className="space-y-8">
            {/* Header + range filter */}
            <div className="flex flex-wrap items-end gap-4 justify-between">
                <div>
                    <div className="label-mono text-[var(--gold)]">— CPS 2026 CAMPAIGN PERFORMANCE</div>
                    <h2 className="serif-display text-3xl md:text-4xl mt-2">Paid applications, sourced.</h2>
                    <p className="text-sm text-[var(--ivory)]/60 mt-2">
                        Primary KPI: <span className="text-[var(--gold)] font-semibold">Paid Applications</span>. Numbers reflect events + payments in the selected date range.
                    </p>
                </div>
                <div className="flex flex-wrap items-end gap-3">
                    <div className="flex border border-[rgba(255,255,240,0.15)]" role="tablist">
                        {RANGE_OPTIONS.map((r) => (
                            <button
                                key={r.key}
                                onClick={() => setRange(r.key)}
                                className={`px-3 py-2 label-mono text-[10px] border-r last:border-r-0 border-[rgba(255,255,240,0.15)] ${range === r.key ? "bg-[var(--gold)] text-[var(--navy-deep)]" : "text-[var(--ivory)]/70 hover:text-[var(--ivory)]"}`}
                                data-testid={`campaign-range-${r.key}`}
                            >
                                {r.label}
                            </button>
                        ))}
                    </div>
                    {range === "custom" && (
                        <div className="flex items-end gap-2">
                            <div>
                                <div className="label-mono text-[10px] text-[var(--ivory)]/50 mb-1">Start</div>
                                <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="input-editorial max-w-[160px]" data-testid="campaign-range-start" />
                            </div>
                            <div>
                                <div className="label-mono text-[10px] text-[var(--ivory)]/50 mb-1">End</div>
                                <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="input-editorial max-w-[160px]" data-testid="campaign-range-end" />
                            </div>
                            <button onClick={load} className="btn-gold" data-testid="campaign-range-apply">Apply</button>
                        </div>
                    )}
                </div>
            </div>

            {loading && <div className="label-mono text-[var(--ivory)]/60">Loading…</div>}

            {a && (
                <>
                    {/* Primary KPI row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-[rgba(255,255,240,0.12)]" data-testid="campaign-primary-kpis">
                        <KpiCard
                            icon={CheckCircle2}
                            label="Paid Applications"
                            value={fmtNum(a.paid_applications)}
                            sub="Primary KPI"
                            primary
                            testid="kpi-paid-applications"
                        />
                        <KpiCard icon={TrendingUp} label="Conversion Rate" value={fmtPct(a.conversion_rate)} sub={`${fmtNum(a.landing_page_visitors_unique)} unique visitors`} testid="kpi-conversion-rate" />
                        <KpiCard icon={IndianRupee} label="India ₹500 payments" value={fmtNum(a.india_paid)} sub={fmtMoney("INR", a.revenue_by_currency?.INR || 0)} testid="kpi-india-paid" />
                        <KpiCard icon={DollarSign} label="International $10 payments" value={fmtNum(a.international_paid)} sub={fmtMoney("USD", a.revenue_by_currency?.USD || 0)} testid="kpi-intl-paid" />
                    </div>

                    {/* Funnel row */}
                    <div className="grid grid-cols-2 md:grid-cols-5 border-t border-l border-[rgba(255,255,240,0.12)]" data-testid="campaign-funnel">
                        <KpiCard icon={Target} label="Landing Views" value={fmtNum(a.landing_page_views)} sub={`${fmtNum(a.landing_page_visitors_unique)} unique`} testid="kpi-landing" />
                        <KpiCard icon={MousePointerClick} label="Apply Clicks" value={fmtNum(a.apply_clicks)} testid="kpi-apply-clicks" />
                        <KpiCard icon={ClipboardList} label="Applications Started" value={fmtNum(a.applications_started)} testid="kpi-app-started" />
                        <KpiCard icon={ShieldCheck} label="Verifications Completed" value={fmtNum(a.verifications_completed)} testid="kpi-verified" />
                        <KpiCard icon={CreditCard} label="Payments Initiated" value={fmtNum(a.payments_initiated)} testid="kpi-pay-initiated" />
                    </div>

                    {/* Provider split */}
                    <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-[rgba(255,255,240,0.12)]" data-testid="campaign-provider-split">
                        <KpiCard icon={CreditCard} label="Razorpay conversions" value={fmtNum(a.by_provider?.razorpay)} testid="kpi-razorpay" />
                        <KpiCard icon={CreditCard} label="PayPal conversions" value={fmtNum(a.by_provider?.paypal)} testid="kpi-paypal" />
                        <KpiCard icon={Calendar} label="Range start" value={new Date(data.range.start).toLocaleDateString()} sub={data.range.key} testid="kpi-range-start" />
                        <KpiCard icon={Calendar} label="Range end" value={new Date(data.range.end).toLocaleDateString()} testid="kpi-range-end" />
                    </div>

                    {/* Ad Spend admin panel */}
                    <AdSpendPanel onChange={load} />

                    {/* UTM breakdowns */}
                    <div className="grid gap-6">
                        <BreakdownTable title="By UTM Source" rows={data.by_utm_source} keyLabel="utm_source" testid="breakdown-utm-source" />
                        <BreakdownTable title="By UTM Medium" rows={data.by_utm_medium} keyLabel="utm_medium" testid="breakdown-utm-medium" />
                        <CampaignPerformanceTable rows={data.by_utm_campaign} thresholds={data.thresholds} />
                        <BreakdownTable title="By UTM Content / Creative" rows={data.by_utm_content} keyLabel="utm_content" testid="breakdown-utm-content" />
                        <BreakdownTable title="By Google Keyword (utm_term)" rows={data.by_keyword} keyLabel="utm_term" testid="breakdown-keyword" />
                    </div>
                </>
            )}
        </div>
    );
}
